/** Creator delete of one archived Resource, retried with its original operation. */
import { ApiError, apiCurrentVersion, apiErrorCode, deleteCoachResource, isCurrentResourceCommandScope } from './api';
import { invalidateResourceCommands, removeDeletedResource } from './queryClient';
import type { ResourceReadScope } from './types';

export type ResourceDeletionOutcome =
  | { status: 'deleted' }
  /** Already absent (deleted earlier, e.g. after a lost acknowledgement). */
  | { status: 'gone' }
  /** Newer version; `currentVersion` is set only when the server authorized it. */
  | { status: 'stale'; currentVersion: number | null }
  | { status: 'rejected'; code: string | null }
  /** No acknowledgement: send again with the same operation. */
  | { status: 'unknown' }
  /** The team/season/actor changed; nothing was sent. */
  | { status: 'unavailable' };

export function createResourceDeletion(scope: ResourceReadScope, resourceId: string, expectedVersion: number) {
  const captured = { ...scope };
  let operationId = crypto.randomUUID(), version = expectedVersion;
  const refresh = () => { void removeDeletedResource(captured, resourceId).catch(() => {}); };
  return {
    /** Target a newer version after the user confirms again: a new operation. */
    rebase(next: number) { version = next; operationId = crypto.randomUUID(); },
    async send(): Promise<ResourceDeletionOutcome> {
      if (!isCurrentResourceCommandScope(captured)) return { status: 'unavailable' };
      try {
        await deleteCoachResource(resourceId, { schema_version: 'resource-delete.v1', operation_id: operationId,
          expected_version: version }, { scope: captured });
      } catch (error) {
        const code = apiErrorCode(error);
        // Only a committed delete retires its key, so a retired retry succeeded.
        if (code !== 'resource_operation_retired') {
          if (code === 'resource_unavailable') { refresh(); return { status: 'gone' }; }
          if (code === 'resource_version_conflict') {
            // Rows cache their version; refresh them so a reopened dialog is current.
            void invalidateResourceCommands(captured).catch(() => {});
            return { status: 'stale', currentVersion: apiCurrentVersion(error) };
          }
          return error instanceof ApiError && error.status >= 400 && error.status < 500
            ? { status: 'rejected', code } : { status: 'unknown' };
        }
      }
      refresh();
      return { status: 'deleted' };
    },
  };
}
